import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Pagination,
  Dialog, DialogContent, Tooltip
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import adminAxios from '../../utils/adminAxios';

const LIMIT = 15;

const dinero = (n) =>
  '$' + (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const METODO_LABEL = { card: 'Tarjeta', spei: 'SPEI', tienda: 'Tienda' };

const STATUS_PAGO = {
  pendiente: { label: 'Pendiente de pago', bg: '#FFF1D6', color: '#A8740F' },
  retenido: { label: 'En custodia', bg: '#EEF2FF', color: '#3730A3' },
  liberado: { label: 'Dispersado', bg: '#D6F3E8', color: '#1B7A5A' },
  reembolsado: { label: 'Reembolsado', bg: '#E0E7FF', color: '#3730A3' },
  devolucion_pendiente: { label: 'Devolución pendiente', bg: '#FFF1D6', color: '#A8740F' },
  cancelado: { label: 'Cancelado', bg: '#F3F4F6', color: '#6B7280' },
  fallido: { label: 'Fallido', bg: '#FFE0DD', color: '#B23B33' },
  contracargo: { label: 'Contracargo', bg: '#FFE0DD', color: '#B23B33' },
};

const StatCard = ({ icono, titulo, valor, sub }) => (
  <Box sx={{ flex: 1, minWidth: 210, bgcolor: 'white', borderRadius: 3, border: '1px solid #F1F5F9', p: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7495F8' }}>
        {icono}
      </Box>
      <Typography sx={{ fontSize: 13, color: '#737791', fontWeight: 600 }}>{titulo}</Typography>
    </Box>
    <Typography sx={{ fontSize: 26, fontWeight: 800, color: 'primary.main' }}>{valor}</Typography>
    {sub && <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{sub}</Typography>}
  </Box>
);

const EstadoPago = ({ status }) => {
  const st = STATUS_PAGO[status] || STATUS_PAGO.cancelado;
  return (
    <Chip label={st.label} size="small"
      sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: 11, borderRadius: '6px' }} />
  );
};

const nombreDe = (u) => (u ? `${u.name || ''} ${u.apellido || ''}`.trim() || u.email : '—');

const fechaCorta = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Finanzas = () => {
  const [tab, setTab] = useState('transacciones');
  const [transacciones, setTransacciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // { accion: 'dispersar'|'devolver', pago }
  const [procesando, setProcesando] = useState(false);
  const [corteLoading, setCorteLoading] = useState(false);
  const [proximoCorte, setProximoCorte] = useState(null); // fecha del siguiente corte automático
  const [page, setPage] = useState(1);

  const cargarFinanzas = () =>
    adminAxios.get('/api/admin/finanzas')
      .then(res => {
        setTransacciones(res.data.transacciones || []);
        setProximoCorte(res.data.proximoCorte || null);
      })
      .catch(() => setToast({ tipo: 'error', msg: 'No se pudo cargar la información financiera.' }));

  useEffect(() => {
    cargarFinanzas().finally(() => setCargando(false));
  }, []);

  useEffect(() => { setPage(1); }, [tab]);

  // Todo se deriva de las transacciones para que las acciones actualicen al momento.
  // Por dispersar: servicios completados + retenciones por cancelación con penalización
  const porDispersar = useMemo(
    () => transacciones.filter(t => t.status === 'retenido' && ['completada', 'cancelada'].includes(t.booking?.status)),
    [transacciones]
  );
  const devoluciones = useMemo(
    () => transacciones.filter(t => t.status === 'devolucion_pendiente'),
    [transacciones]
  );
  const stats = useMemo(() => {
    const suma = (lista, campo) => Math.round(lista.reduce((acc, p) => acc + (p[campo] || 0), 0) * 100) / 100;
    const retenidos = transacciones.filter(t => t.status === 'retenido');
    const liberados = transacciones.filter(t => t.status === 'liberado');
    return {
      enCustodia: suma(retenidos, 'monto'),
      comisiones: suma([...retenidos, ...liberados], 'comisionMonto'),
      dispersado: suma(liberados, 'montoProveedor'),
      porDispersar: suma(porDispersar, 'montoProveedor'),
      devolucionesPendientes: suma(devoluciones, 'monto'),
    };
  }, [transacciones, porDispersar, devoluciones]);

  const actualizarPago = (id, cambios) =>
    setTransacciones(prev => prev.map(t => (t._id === id ? { ...t, ...cambios } : t)));

  const ejecutarAccion = async () => {
    const { accion, pago } = confirmar;
    setProcesando(true);
    try {
      if (accion === 'dispersar') {
        await adminAxios.patch(`/api/admin/finanzas/${pago._id}/dispersar`);
        actualizarPago(pago._id, { status: 'liberado' });
        setToast({ tipo: 'success', msg: `Se transfirieron ${dinero(pago.montoProveedor)} a la cuenta del proveedor. Fue notificado.` });
      } else {
        await adminAxios.patch(`/api/admin/finanzas/${pago._id}/devolver`);
        actualizarPago(pago._id, { status: 'reembolsado' });
        setToast({ tipo: 'success', msg: 'Devolución marcada como completada.' });
      }
      setConfirmar(null);
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudo completar la operación.' });
    } finally {
      setProcesando(false);
    }
  };

  // Corte manual: dispersa ya todo lo retenido que cumplió su retención y sin disputa,
  // sin esperar al día de corte semanal.
  const ejecutarCorte = async () => {
    setCorteLoading(true);
    try {
      const res = await adminAxios.post('/api/admin/finanzas/corte');
      const n = res.data?.dispersados || 0;
      setToast({ tipo: n ? 'success' : 'info', msg: n ? `Corte ejecutado: ${n} pago(s) dispersado(s).` : 'No hay pagos listos para dispersar en este momento.' });
      await cargarFinanzas();
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudo ejecutar el corte.' });
    } finally {
      setCorteLoading(false);
    }
  };

  // Estado de retención de un pago: listo para corte o fecha en que se libera
  const retencion = (t) => {
    const desde = t.dispersarDesde ? new Date(t.dispersarDesde).getTime() : null;
    if (desde == null) return { listo: false, label: 'En custodia' };
    if (desde <= Date.now()) return { listo: true, label: 'Listo para corte' };
    return { listo: false, label: `Libera ${fechaCorta(t.dispersarDesde)}` };
  };

  const cabeceraSx = { color: '#94A3B8', fontWeight: 700, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', borderBottom: '1px solid #F1F5F9' };
  const celdaSx = { borderBottom: '1px solid #F6F8FB', py: 1.5 };

  const TABS = [
    { key: 'transacciones', label: 'Transacciones', badge: 0 },
    { key: 'dispersar', label: 'Por dispersar', badge: porDispersar.length },
    { key: 'devoluciones', label: 'Devoluciones', badge: devoluciones.length },
  ];

  const listaActual = tab === 'transacciones' ? transacciones
    : tab === 'dispersar' ? porDispersar
    : devoluciones;
  const totalPaginas = Math.max(1, Math.ceil(listaActual.length / LIMIT));
  const inicio = (page - 1) * LIMIT;
  const pagina = listaActual.slice(inicio, inicio + LIMIT);

  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Métricas */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard icono={<AccountBalanceWalletOutlinedIcon />} titulo="En custodia" valor={dinero(stats.enCustodia)} sub="Cobrado, pendiente de completar servicio" />
        <StatCard icono={<SendOutlinedIcon />} titulo="Por dispersar" valor={dinero(stats.porDispersar)} sub={`${porDispersar.length} pago(s) de servicios completados`} />
        <StatCard icono={<SavingsOutlinedIcon />} titulo="Comisiones ganadas" valor={dinero(stats.comisiones)} sub="10% de cada transacción cobrada" />
        <StatCard icono={<ReplayOutlinedIcon />} titulo="Devoluciones pendientes" valor={dinero(stats.devolucionesPendientes)} sub={`${devoluciones.length} por procesar manualmente`} />
      </Box>

      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <Button key={t.key} onClick={() => setTab(t.key)}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2.5,
              bgcolor: tab === t.key ? 'secondary.main' : 'white',
              color: tab === t.key ? 'white' : '#737791',
              border: '1px solid', borderColor: tab === t.key ? 'secondary.main' : '#F1F5F9',
              '&:hover': { bgcolor: tab === t.key ? 'secondary.main' : '#F8FAFC' }
            }}>
            {t.label}
            {t.badge > 0 && (
              <Box component="span" sx={{
                ml: 1, px: 1, borderRadius: '10px', fontSize: 11, fontWeight: 800,
                bgcolor: tab === t.key ? 'rgba(255,255,255,0.25)' : '#FFF1D6',
                color: tab === t.key ? 'white' : '#A8740F'
              }}>
                {t.badge}
              </Box>
            )}
          </Button>
        ))}
        <Box sx={{ flexGrow: 1 }} />
        {proximoCorte && (
          <Typography variant="caption" sx={{ color: '#737791', fontWeight: 600, alignSelf: 'center', mr: 1.5, whiteSpace: 'nowrap' }}>
            Próximo corte automático: {fechaCorta(proximoCorte)}
          </Typography>
        )}
        <Button
          onClick={ejecutarCorte}
          disabled={corteLoading}
          startIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2,
            bgcolor: 'primary.main', color: 'white',
            '&:hover': { bgcolor: '#0a1a4a' }, '&.Mui-disabled': { bgcolor: '#94A3B8', color: 'white' }
          }}>
          {corteLoading ? 'Procesando…' : 'Ejecutar corte ahora'}
        </Button>
      </Box>

      {/* Tabla */}
      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #F1F5F9', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          {tab === 'transacciones' && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={cabeceraSx}>Fecha</TableCell>
                  <TableCell sx={cabeceraSx}>Servicio</TableCell>
                  <TableCell sx={cabeceraSx}>Cliente</TableCell>
                  <TableCell sx={cabeceraSx}>Proveedor</TableCell>
                  <TableCell sx={cabeceraSx}>Método</TableCell>
                  <TableCell sx={cabeceraSx} align="right">Monto</TableCell>
                  <TableCell sx={cabeceraSx} align="right">Comisión</TableCell>
                  <TableCell sx={cabeceraSx}>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagina.map(t => (
                  <TableRow key={t._id} hover>
                    <TableCell sx={celdaSx}>{fechaCorta(t.createdAt)}</TableCell>
                    <TableCell sx={celdaSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>
                        {t.booking?.servicioNombre || 'Servicio'}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                        {t.booking?.fecha} {t.booking?.hora}
                      </Typography>
                    </TableCell>
                    <TableCell sx={celdaSx}>{nombreDe(t.cliente)}</TableCell>
                    <TableCell sx={celdaSx}>{nombreDe(t.proveedor)}</TableCell>
                    <TableCell sx={celdaSx}>
                      {METODO_LABEL[t.metodo] || t.metodo}
                      {t.metodo === 'card' && t.tarjeta?.last4 ? ` •${t.tarjeta.last4}` : ''}
                    </TableCell>
                    <TableCell sx={celdaSx} align="right">
                      <strong>{dinero(t.monto)}</strong>
                    </TableCell>
                    <TableCell sx={{ ...celdaSx, color: '#1B7A5A' }} align="right">{dinero(t.comisionMonto)}</TableCell>
                    <TableCell sx={celdaSx}>
                      <Tooltip title={t.errorPago || ''}>
                        <span><EstadoPago status={t.status} /></span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {pagina.length === 0 && (
                  <TableRow><TableCell colSpan={8} sx={{ ...celdaSx, textAlign: 'center', color: '#94A3B8', py: 5 }}>
                    Aún no hay transacciones.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {tab === 'dispersar' && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={cabeceraSx}>Servicio</TableCell>
                  <TableCell sx={cabeceraSx}>Proveedor</TableCell>
                  <TableCell sx={cabeceraSx}>Cuenta Stripe</TableCell>
                  <TableCell sx={cabeceraSx} align="right">Cobrado</TableCell>
                  <TableCell sx={cabeceraSx} align="right">A dispersar (90%)</TableCell>
                  <TableCell sx={cabeceraSx} align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagina.map(t => (
                  <TableRow key={t._id} hover>
                    <TableCell sx={celdaSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>
                        {t.booking?.servicioNombre || 'Servicio'}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: retencion(t).listo ? '#1B7A5A' : '#A8740F', fontWeight: 600 }}>
                        {retencion(t).label}
                      </Typography>
                    </TableCell>
                    <TableCell sx={celdaSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>{nombreDe(t.proveedor)}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{t.proveedor?.email}</Typography>
                    </TableCell>
                    <TableCell sx={celdaSx}>
                      {t.proveedor?.connect?.listo
                        ? <Chip label="Conectada" size="small" sx={{ bgcolor: '#D6F3E8', color: '#1B7A5A', fontWeight: 700, fontSize: 11 }} />
                        : t.proveedor?.connect?.conectado
                          ? <Chip label="Verificando" size="small" sx={{ bgcolor: '#FFF1D6', color: '#A8740F', fontWeight: 700, fontSize: 11 }} />
                          : <Chip label="Sin conectar" size="small" sx={{ bgcolor: '#FFE0DD', color: '#B23B33', fontWeight: 700, fontSize: 11 }} />}
                    </TableCell>
                    <TableCell sx={celdaSx} align="right">{dinero(t.monto)}</TableCell>
                    <TableCell sx={celdaSx} align="right"><strong>{dinero(t.montoProveedor)}</strong></TableCell>
                    <TableCell sx={celdaSx} align="center">
                      <Tooltip title={t.proveedor?.connect?.listo ? '' : 'El proveedor aún no conectó su cuenta con Stripe'}>
                        <span>
                          <Button size="small" variant="contained" disableElevation
                            disabled={!t.proveedor?.connect?.listo}
                            onClick={() => setConfirmar({ accion: 'dispersar', pago: t })}
                            startIcon={<SendOutlinedIcon sx={{ fontSize: 14 }} />}
                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: 12 }}>
                            Dispersar
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {pagina.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ ...celdaSx, textAlign: 'center', color: '#94A3B8', py: 5 }}>
                    No hay pagos pendientes de dispersar.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {tab === 'devoluciones' && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={cabeceraSx}>Servicio</TableCell>
                  <TableCell sx={cabeceraSx}>Cliente</TableCell>
                  <TableCell sx={cabeceraSx}>Método</TableCell>
                  <TableCell sx={cabeceraSx}>Motivo</TableCell>
                  <TableCell sx={cabeceraSx} align="right">Monto a devolver</TableCell>
                  <TableCell sx={cabeceraSx} align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagina.map(t => (
                  <TableRow key={t._id} hover>
                    <TableCell sx={celdaSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>
                        {t.booking?.servicioNombre || 'Servicio'}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{t.booking?.fecha}</Typography>
                    </TableCell>
                    <TableCell sx={celdaSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>{nombreDe(t.cliente)}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{t.cliente?.email}</Typography>
                    </TableCell>
                    <TableCell sx={celdaSx}>{METODO_LABEL[t.metodo] || t.metodo}</TableCell>
                    <TableCell sx={{ ...celdaSx, maxWidth: 260 }}>
                      <Typography sx={{ fontSize: 12, color: '#737791' }}>{t.errorPago || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={celdaSx} align="right"><strong>{dinero(t.monto)}</strong></TableCell>
                    <TableCell sx={celdaSx} align="center">
                      <Button size="small" variant="contained" disableElevation color="success"
                        onClick={() => setConfirmar({ accion: 'devolver', pago: t })}
                        startIcon={<CheckRoundedIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: 12, color: 'white' }}>
                        Marcar devuelta
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {pagina.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ ...celdaSx, textAlign: 'center', color: '#94A3B8', py: 5 }}>
                    No hay devoluciones pendientes.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}

        </Box>

        {/* Paginación */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', px: 3, py: 2 }}>
          <Pagination
            count={totalPaginas} page={page} onChange={(_, v) => setPage(v)}
            color="secondary" shape="rounded"
            sx={{ '& .MuiPaginationItem-root': { fontSize: 14 } }}
          />
          <Typography sx={{ position: 'absolute', right: 24, fontSize: 12, color: '#94A3B8' }}>
            Mostrando {listaActual.length === 0 ? 0 : inicio + 1}–{Math.min(inicio + LIMIT, listaActual.length)} de {listaActual.length}
          </Typography>
        </Box>
      </Box>

      {/* Confirmación de acciones */}
      <Dialog open={!!confirmar} onClose={() => !procesando && setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ mb: 1 }}>
            {confirmar?.accion === 'dispersar' ? 'Dispersar pago al proveedor'
              : 'Confirmar devolución'}
          </Typography>
          {confirmar?.accion === 'dispersar' ? (
            <>
              <Typography variant="body2" color="#737791" sx={{ mb: 2 }}>
                Se transferirá automáticamente el 90% a la cuenta de Stripe de{' '}
                <strong>{nombreDe(confirmar?.pago?.proveedor)}</strong>. El pago quedará como
                dispersado y el proveedor recibirá la notificación.
              </Typography>
              <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: 2, p: 2, mb: 3 }}>
                <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Monto a transferir (90%)</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'primary.main' }}>
                  {dinero(confirmar?.pago?.montoProveedor)}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="#737791" sx={{ mb: 3 }}>
              {`Confirma que ya devolviste ${dinero(confirmar?.pago?.monto)} a ${nombreDe(confirmar?.pago?.cliente)} (transferencia manual). El registro quedará como reembolsado.`}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => setConfirmar(null)} disabled={procesando} sx={{ color: '#737791', textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={ejecutarAccion} disabled={procesando}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
              {procesando ? 'Procesando…' : confirmar?.accion === 'dispersar' ? 'Transferir ahora' : 'Confirmar'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default Finanzas;
